import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import COEHeaderComponent from '../../components/coe-header/coe-header.component';
import ProgramResultCard from '../../components/program-billing/program-result-card.component';
import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import ProgramsService from '../../services/programs.service';
import { Program } from '../../models/billing.models';
import { clearPendingPaidTeam, formatMoney, intervalShort, readPendingPaidTeam } from '../../helpers/billing.helpers';

const BillingConfirmationPage: FC = () => {
  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState<Program | null>(null);
  const programsService = useService(ProgramsService);
  const { showSnackbar } = useSnackbarHelper();
  const navigate = useNavigate();
  const confirmed = useRef(false);

  useEffect(() => {
    if (confirmed.current) return;
    confirmed.current = true;
    const params = new URLSearchParams(window.location.search);
    const paymentIntentId = params.get('payment_intent');
    const setupIntentId = params.get('setup_intent');
    const draft = readPendingPaidTeam();
    if (params.get('redirect_status') === 'failed') {
      showSnackbar('Payment did not go through. Please try again.', 'error');
      navigate('/billing');
      return;
    }
    if (setupIntentId && draft?.planKey && draft.billingInterval && !draft.programId) {
      programsService
        .startPaidSeason({
          planKey: draft.planKey,
          billingInterval: draft.billingInterval,
          teamName: draft.teamName,
          teamColor: draft.teamColor,
          teamDescription: draft.teamDescription,
          setupIntentId,
        })
        .then((response) => {
          const created = response.data.data;
          if (created.pending) {
            showSnackbar('Your bank still needs to approve this card. Open Create team and try again.', 'error');
            if (created.subscriptionId) {
              programsService.abandonPaidCheckout(created.subscriptionId).catch(() => undefined);
            }
            clearPendingPaidTeam();
            navigate('/teams');
            return;
          }
          clearPendingPaidTeam();
          if (created.teamId) {
            navigate(`/create-team/success?team_id=${created.teamId}`);
            return;
          }
          navigate('/billing');
        })
        .catch(() => {
          showSnackbar('Unable to verify payment. Please reach out to support', 'error');
          navigate('/billing');
        });
      return;
    }
    if (paymentIntentId && draft?.subscriptionId && draft.planKey && draft.billingInterval && !draft.programId) {
      programsService
        .finalizePaidSeason({
          subscriptionId: draft.subscriptionId,
          planKey: draft.planKey,
          billingInterval: draft.billingInterval,
          teamName: draft.teamName,
          teamColor: draft.teamColor,
          teamDescription: draft.teamDescription,
        })
        .then((response) => {
          clearPendingPaidTeam();
          const teamId = response.data.data.teamId;
          navigate(teamId ? `/create-team/success?team_id=${teamId}` : '/billing');
        })
        .catch(() => {
          showSnackbar('Unable to verify payment. Please reach out to support', 'error');
          navigate('/billing');
        });
      return;
    }
    if (!paymentIntentId) {
      showSnackbar('Missing payment confirmation', 'error');
      navigate('/billing');
      return;
    }
    programsService
      .confirmPayment(paymentIntentId)
      .then((confirmedPayment) => {
        const programId = (confirmedPayment.data.data as { programId?: number } | undefined)?.programId;
        return programsService.listPrograms().then((listed) => {
          const openPrograms = listed.data.data || [];
          return openPrograms.find((item) => item.id === programId) || null;
        });
      })
      .then((nextProgram) => {
        const pendingTeam = readPendingPaidTeam();
        if (
          pendingTeam &&
          nextProgram?.id === pendingTeam.programId &&
          nextProgram.status === 'active' &&
          nextProgram.teams[0]
        ) {
          clearPendingPaidTeam();
          navigate(`/create-team/success?team_id=${nextProgram.teams[0].id}`);
          return;
        }
        setProgram(nextProgram);
        setLoading(false);
      })
      .catch(() => {
        showSnackbar('Unable to verify payment. Please reach out to support', 'error');
        navigate('/billing');
      });
  }, []);

  const renderSummary = () => {
    if (!program) return null;
    const perSquad = program.extraTeamPrice || 0;
    const listTotal = program.listPrice != null ? program.listPrice + program.extraTeams * perSquad : null;
    const paidTotal = listTotal;
    return (
      <>
        <ul className="pb-result-rows">
          <li>
            <span>Plan</span>
            <b>
              {program.planName} · {program.billingInterval === 'year' ? 'Yearly' : 'Monthly'}
            </b>
          </li>
          <li>
            <span>Price</span>
            <b>
              {formatMoney(paidTotal)}
              {intervalShort(program.billingInterval)}
            </b>
          </li>
          {program.currentPeriodEnd ? (
            <li>
              <span>Next payment</span>
              <b>{dayjs(program.currentPeriodEnd).format('MMM D, YYYY')}</b>
            </li>
          ) : null}
          <li>
            <span>Teams</span>
            <b>
              {program.teamCount} {program.teamCount === 1 ? 'team' : 'teams'} active
            </b>
          </li>
        </ul>
        <div className="pb-callout">Your receipt is in Payment History on the Billing page.</div>
      </>
    );
  };

  return (
    <div className={window.localStorage.getItem('sidedrawer') ? 'page-full' : 'page'} id="page">
      <COEHeaderComponent title="Billing confirmation" />
      {loading ? (
        <ProgramResultCard
          pending
          eyebrow="Almost done"
          title="Confirming your payment…"
          subtitle="This only takes a moment. Please keep this page open."
        />
      ) : (
        <ProgramResultCard
          eyebrow="Payment received"
          title="You're subscribed"
          subtitle="Thanks for subscribing. Every team on your plan is unlocked."
          actions={
            <>
              <button type="button" className="pb-btn pb-btn-secondary" onClick={() => navigate('/billing')}>
                View billing
              </button>
              <button type="button" className="pb-btn pb-btn-primary" onClick={() => navigate('/teams')}>
                Go to My Teams
              </button>
            </>
          }
        >
          {renderSummary()}
        </ProgramResultCard>
      )}
    </div>
  );
};

export default BillingConfirmationPage;
